import { useQueryHelper } from '@anytag/shared-hooks';
import { useExistingProfileTagsLazyQuery } from '@anytag/graphql';
import { AsyncSelectorSearchField, AsyncSelectorSearchFieldProps } from '../async-selector-search-field';
import {
  TagSearchOption,
  AsyncSearchTagsFieldProps,
  SingleAsyncSearchTagsFieldProps,
  MultipleAsyncSearchTagsFieldProps
} from './types';

// Function overloads for proper type inference based on `multiple` prop
export function AsyncSearchTagsField<T extends string>(props: MultipleAsyncSearchTagsFieldProps<T>): React.ReactElement;
export function AsyncSearchTagsField<T extends string>(props: SingleAsyncSearchTagsFieldProps<T>): React.ReactElement;
export function AsyncSearchTagsField<T extends string>({
  name,
  title,
  multiple,
  searchProps,
  ...restProps
}: AsyncSearchTagsFieldProps<T>): React.ReactElement {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { getExistingProfileTags, loading } = useExistingProfileTagsLazyQuery();

  const fetchTags: AsyncSelectorSearchFieldProps<T, TagSearchOption>['onSearch'] = async (keyword) => {
    let tags: TagSearchOption[] = [];

    try {
      const { data } = await getExistingProfileTags({ variables: { keyword: keyword ?? '' } });
      tags = data?.existingProfileTags?.tags.map((tag) => ({ value: tag, label: tag })) || [];
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }

    return tags;
  };

  const commonProps: Pick<
    AsyncSelectorSearchFieldProps<T, TagSearchOption>,
    'name' | 'loading' | 'onSearch' | 'title' | 'searchProps'
  > = {
    name,
    loading,
    onSearch: fetchTags,
    title: title ?? t<string>('Selector.Tags'),
    searchProps: { placeholder: t<string>('Search by tag name'), ...searchProps }
  };

  return multiple ? (
    <AsyncSelectorSearchField<T, TagSearchOption>
      {...(restProps as Partial<MultipleAsyncSearchTagsFieldProps<T>>)}
      {...commonProps}
      multiple={true}
    />
  ) : (
    <AsyncSelectorSearchField<T, TagSearchOption>
      {...(restProps as Partial<SingleAsyncSearchTagsFieldProps<T>>)}
      {...commonProps}
      multiple={false}
    />
  );
}
